/**
 * Hook for paginated projects list using cursor-based pagination
 * Uses useInfiniteQuery from @tanstack/react-query
 */

import { useInfiniteQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import type { ProjectListResponse } from '../types/project';

const PAGE_SIZE = 20;

export const projectsQueryKey = ['projects'] as const;

const fetchProjects = async (cursor?: string): Promise<ProjectListResponse> => {
  const params = new URLSearchParams();
  params.set('pageSize', String(PAGE_SIZE));
  if (cursor) {
    params.set('cursor', cursor);
  }

  // apiFn wraps handler response in { data: <handler_return>, timestamp, requestId }
  // The axios interceptor unwraps the axios response, so we get the HTTP body.
  // We need .data to unwrap the apiFn wrapper and get { data: [...], pagination: {} }
  const response = await api.get(`/projects?${params.toString()}`);
  return (response as { data: ProjectListResponse }).data;
};

/**
 * Paginated project list. Pass `enabled: false` to skip the fetch entirely
 * (e.g. 社内 users, whose sidebar has no project list to populate).
 */
export function useProjectsList({ enabled = true }: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: projectsQueryKey,
    queryFn: ({ pageParam }) => fetchProjects(pageParam),
    getNextPageParam: (lastPage) => lastPage?.pagination?.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled,
  });
}
